/* eslint-disable react/no-danger */
/* eslint-disable camelcase */
import React, { useRef, useState, useEffect, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { FormContext } from '../../customise/contexts/form-context';
import { defaultUserCustomTexts } from '../../../shared/form-lib';
import { Input } from '../../form_fields/common-field-styles';
import {
  PrintPreview,
  PrintActions,
  CopyContainer,
  DefaultButton,
  SubmissionText,
  CreateFormText,
  ResponsesPreview,
  EditFormContainer,
  EditResponseInput,
  PostSubmissionText,
  CreateFormContainer,
  SubmitAnotherAndPrint,
  SubmittedFormContainer,
  SubmitAnotherAndPrintButton,
  PostSubmissionFormContainer,
} from './form-pagination.styled';

const FormFullSubmission = ({
  editPath,
  editResponse,
  redirectLink,
  formSubmitted,
  formResponses,
  handleCopyLink,
  userCustomTexts,
  hasFinalMessage,
  showPrintResponse,
  anotherResponseLink,
  finalRenderedMessage,
  postSubmissionAlignment,
  postSubmissionDirection,
}) => {
  const formResponsesRef = useRef(null);
  const { PLAN } = useContext(FormContext);
  const [counter, setCounter] = useState(10);
  const isInIframe = window.self !== window.top;
  const [isCopied, setIsCopied] = useState(false);
  const [autoRedirect, setAutoRedirect] = useState(true);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const editingResponse = window.location.pathname.includes('/edit/');
  const { name: formName } = useSelector((state) => state.form.editableForm);
  const newResponseLink = editingResponse ? window.location.href.split('/edit')[0] : window.location.href;

  const printResponses = () => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'print');
    iframe.setAttribute('id', 'ifmcontentstoprint');
    iframe.setAttribute('style', 'height: 0px; width: 0px; position: absolute; font-family: Roboto Slab}');

    document.body.appendChild(iframe);
    const printWindow = iframe.contentWindow;

    printWindow.document.open();

    // Write the content of formResponsesRef to the iframe's document
    printWindow.document.write(formResponsesRef.current.innerHTML);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    document.body.removeChild(iframe); // cleanup
  };

  const redirectToAfterLinkOnNewTab = () => {
    setAutoRedirect(false);
    window.open(redirectLink, '_blank');
  };

  useEffect(() => {
    if (isCopied) {
      const timeOutId = setTimeout(() => setIsCopied(false), 2000);
      return () => clearTimeout(timeOutId);
    }
  }, [isCopied]);

  useEffect(() => {
    if (redirectLink && formSubmitted && !isInIframe && autoRedirect) {
      if (counter > 0) {
        const timer = setInterval(() => {
          setCounter((prevCount) => prevCount - 1);
        }, 1000);
        return () => clearInterval(timer);
      }

      if (counter === 0) {
        window.location.replace(redirectLink);
      }
    }
  }, [counter, redirectLink, formSubmitted, autoRedirect]);

  return (
    <PostSubmissionFormContainer>
      {!showPrintPreview && (
        <>
          <SubmittedFormContainer
            more={editResponse || anotherResponseLink || showPrintResponse}
            none={!editResponse && !anotherResponseLink && !showPrintResponse}
            underline={PLAN.is_free}>
            {!hasFinalMessage || (hasFinalMessage && !finalRenderedMessage) ? (
              <>
                {/* --- Default submission message --- */}
                <img src="https://storage.googleapis.com/fplsblog/post_submission_checkmark.svg" alt="" />
                <PostSubmissionText>Thank you!</PostSubmissionText>
                <SubmissionText submitted>Your response has been submitted</SubmissionText>
              </>
            ) : (
              <SubmissionText
                main
                style={{ '--text-align': postSubmissionAlignment, '--direction': postSubmissionDirection }}>
                {/* --- Custom submission message --- */}
                <ReactMarkdown
                  escapeHtml={false}
                  linkTarget="_blank"
                  className="pre-wrap"
                  source={finalRenderedMessage}
                />
              </SubmissionText>
            )}

            {/* --- Redirect response --- */}
            <>
              {redirectLink && (
                <SubmissionText redirect>
                  {(isInIframe || !autoRedirect) ? 'Click to be redirected to ' : 'You will be redirected to '}
                  <span
                    className="redirect-link"
                    role="button"
                    tabIndex={0}
                    onClick={redirectToAfterLinkOnNewTab}
                    onKeyUp={redirectToAfterLinkOnNewTab}>
                    {redirectLink}
                  </span>{' '}
                  {!isInIframe && autoRedirect && counter !== 0 && (
                    <>
                      in {counter} seconds.{' '}
                      <span
                        className="cancel-redirect"
                        role="button"
                        tabIndex={0}
                        onClick={() => setAutoRedirect(false)}
                        onKeyUp={() => setAutoRedirect(false)}>
                        Cancel
                      </span>
                    </>
                  )}
                </SubmissionText>
              )}
            </>
          </SubmittedFormContainer>

          <>
            {(editResponse || anotherResponseLink || showPrintResponse) && (
              <EditFormContainer
                more={editResponse || anotherResponseLink || showPrintResponse}
                none={!editResponse && !anotherResponseLink && !showPrintResponse}>
                <div className="edit-another-print">
                  {editResponse && (
                    <>
                      <DefaultButton onClick={() => window.location.replace(editPath)}>
                        {userCustomTexts.edit_response || defaultUserCustomTexts.edit_response}
                      </DefaultButton>
                      <EditResponseInput>
                        <Input value={editPath} readOnly />
                        <CopyContainer
                          isCopied={isCopied}
                          onClick={(e) => {
                            setIsCopied(true);
                            handleCopyLink(e, editPath);
                          }}>
                          {isCopied ? (
                            <>
                              <img src="https://storage.googleapis.com/fplsblog/post_submission_checkmark.svg" alt="" />
                              <span>Link copied</span>
                            </>
                          ) : (
                            <>
                              <img src="https://storage.googleapis.com/fplsblog/copy.svg" alt="copy-icon" />
                              <span>
                                {userCustomTexts.copy_edit_link_text || defaultUserCustomTexts.copy_edit_link_text}
                              </span>
                            </>
                          )}
                        </CopyContainer>
                      </EditResponseInput>
                    </>
                  )}
                  <SubmitAnotherAndPrint both={anotherResponseLink && showPrintResponse}>
                    {anotherResponseLink && (
                      <SubmitAnotherAndPrintButton
                        both={anotherResponseLink && showPrintResponse}
                        single={anotherResponseLink && !showPrintResponse}
                        onClick={() => window.location.replace(newResponseLink)}>
                        {userCustomTexts.submit_another_response || defaultUserCustomTexts.submit_another_response}
                      </SubmitAnotherAndPrintButton>
                    )}
                    {showPrintResponse && (
                      <SubmitAnotherAndPrintButton
                        both={anotherResponseLink && showPrintResponse}
                        single={!anotherResponseLink && showPrintResponse}
                        onClick={() => setShowPrintPreview(true)}>
                        {userCustomTexts.print_response_text || defaultUserCustomTexts.print_response_text}
                      </SubmitAnotherAndPrintButton>
                    )}
                  </SubmitAnotherAndPrint>
                </div>

                {(PLAN.is_free || PLAN.is_trail) && (
                  <div className="create-form">
                    <CreateFormText>
                      Create your own online survey, forms, and other documents in the <span>easy-to-use </span>Formplus
                      builder
                    </CreateFormText>
                    <a href="https://formpl.us/?utm_source=formplus_form&utm_medium=post-submission">
                      Create a form for free
                    </a>
                  </div>
                )}
              </EditFormContainer>
            )}

            {(PLAN.is_free || PLAN.is_trail) && (
              <CreateFormContainer>
                <CreateFormText>
                  Create your own online survey, forms, and other documents in the <span>easy-to-use </span>Formplus
                  builder
                </CreateFormText>
                <DefaultButton>
                  <a href="https://formpl.us/?utm_source=formplus_form&utm_medium=post-submission">
                    Create a form for free
                  </a>
                </DefaultButton>
              </CreateFormContainer>
            )}
          </>
        </>
      )}

      {/* --- Print preview --- */}
      {showPrintPreview && (
        <PrintPreview>
          <PrintActions id="no_print">
            <button id="print" onClick={printResponses}>
              <i className="print icon" />
              Print
            </button>
            <button onClick={() => setShowPrintPreview(false)}>
              <i className="remove icon" />
              Close
            </button>
          </PrintActions>

          <ResponsesPreview id="formResponses" ref={formResponsesRef}>
            <h2>{formName}</h2>
            <ol>
              {formResponses.length &&
                formResponses.map((response, idx) => {
                  return (
                    <li key={idx}>
                      <p>{response.label}</p>
                      <div dangerouslySetInnerHTML={{ __html: response.value }} />
                    </li>
                  );
                })}
            </ol>
          </ResponsesPreview>
        </PrintPreview>
      )}
    </PostSubmissionFormContainer>
  );
};

FormFullSubmission.defaultProps = {
  editPath: null,
  formResponses: [],
  editResponse: null,
  redirectLink: null,
  formSubmitted: false,
  showPrintResponse: null,
  finalRenderedMessage: null,
  handleCopyLink: () => null,
  anotherResponseLink: false,
};

FormFullSubmission.propTypes = {
  editPath: PropTypes.string,
  editResponse: PropTypes.bool,
  formSubmitted: PropTypes.bool,
  formResponses: PropTypes.array,
  handleCopyLink: PropTypes.func,
  redirectLink: PropTypes.string,
  showPrintResponse: PropTypes.bool,
  anotherResponseLink: PropTypes.bool,
  finalRenderedMessage: PropTypes.string,
  hasFinalMessage: PropTypes.bool.isRequired,
  postSubmissionAlignment: PropTypes.string.isRequired,
  postSubmissionDirection: PropTypes.string.isRequired,
};

export default FormFullSubmission;
